import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getFirestore } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faMagnifyingGlass, faMobileScreenButton } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import defaulProfPic from '../../Images/defprofile.png';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';

  const dateObj = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return 'Not recorded';

  return dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatRelativeDays = (days) => {
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
};

const resolveLastLogin = (data) => {
  const candidates = [
    data.androidLastLoginAt,
    data.lastLoginAt,
    data.androidLastLoginAtMs,
    data.lastLoginAtMs,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (candidate?.toDate) {
      const dateObj = candidate.toDate();
      if (!Number.isNaN(dateObj.getTime())) return dateObj;
    }

    if (candidate?.seconds) {
      const dateObj = new Date(candidate.seconds * 1000);
      if (!Number.isNaN(dateObj.getTime())) return dateObj;
    }

    const dateObj = new Date(candidate);
    if (!Number.isNaN(dateObj.getTime())) return dateObj;
  }

  return null;
};

const getActivityProfile = (lastLoginDate) => {
  if (!lastLoginDate) {
    return {
      label: 'No Android login',
      tone: 'secondary',
      details: 'No mobile sign-in recorded yet.',
      daysSince: null,
    };
  }

  const daysSince = Math.floor((Date.now() - lastLoginDate.getTime()) / DAY_IN_MS);

  if (daysSince <= 1) {
    return {
      label: 'Active today',
      tone: 'success',
      details: 'Logged in within the last 24 hours.',
      daysSince,
    };
  }

  if (daysSince <= 7) {
    return {
      label: 'Active this week',
      tone: 'primary',
      details: 'Logged in within the last 7 days.',
      daysSince,
    };
  }

  if (daysSince <= 30) {
    return {
      label: 'Idle',
      tone: 'warning',
      details: 'Logged in within the last 30 days.',
      daysSince,
    };
  }

  return {
    label: 'Dormant',
    tone: 'danger',
    details: 'Last mobile sign-in was more than 30 days ago.',
    daysSince,
  };
};

function AndroidAppActivity() {
  const db = getFirestore(app);
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState('All');

  const fetchAndroidAppStudents = useCallback(async () => {
    setLoading(true);

    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const items = [];

      snapshot.forEach((docItem) => {
        const data = docItem.data();
        const userType = String(data.userType || '').trim().toLowerCase();
        const userStatus = String(data.userStatus || '').trim().toLowerCase();
        const isStudent = userType === 'student' || userStatus === 'student';
        const signedInViaAndroid = data.signedInViaAndroid === true
          || String(data.lastSignInPlatform || '').trim().toLowerCase() === 'android';
        const lastLoginDate = resolveLastLogin(data);

        if (!isStudent || (!signedInViaAndroid && !lastLoginDate)) {
          return;
        }

        const lastLoginMs = lastLoginDate ? lastLoginDate.getTime() : 0;

        items.push({
          id: docItem.id,
          userId: data.userId || docItem.id,
          bio: data.bio || 'Unnamed student',
          email: data.email || data.userEmail || '',
          course: data.course || 'Course not set',
          phoneNumber: data.phoneNumber || '',
          profilePic: data.profilePic || '',
          signedInViaAndroid,
          lastLoginDate,
          lastLoginMs,
          loginPlatform: data.lastSignInPlatform || 'android',
        });
      });

      items.sort((first, second) => (second.lastLoginMs || 0) - (first.lastLoginMs || 0));
      setStudents(items);
    } catch (error) {
      console.error('Failed to load Android app students:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchAndroidAppStudents();
  }, [fetchAndroidAppStudents]);

  const filteredStudents = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();

    return students.filter((student) => {
      const activityProfile = getActivityProfile(student.lastLoginDate);

      if (activityFilter !== 'All' && activityProfile.label !== activityFilter) {
        return false;
      }

      if (!queryText) {
        return true;
      }

      const haystack = [
        student.bio,
        student.email,
        student.course,
        student.phoneNumber,
        student.loginPlatform,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(queryText);
    });
  }, [activityFilter, searchTerm, students]);

  const stats = useMemo(() => {
    const breakdown = {
      today: 0,
      week: 0,
      idle: 0,
      dormant: 0,
    };

    students.forEach((student) => {
      const profile = getActivityProfile(student.lastLoginDate);
      if (profile.label === 'Active today') {
        breakdown.today += 1;
      } else if (profile.label === 'Active this week') {
        breakdown.week += 1;
      } else if (profile.label === 'Idle') {
        breakdown.idle += 1;
      } else if (profile.label === 'Dormant') {
        breakdown.dormant += 1;
      }
    });

    return breakdown;
  }, [students]);

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage">
          <div className="appsHeader">
            <div>
              <p className="appsKicker">Data Analytics</p>
              <h4>Android App Activity</h4>
              <p className="appsSubtext">
                Students who have signed in through the Android app, grouped by how recently they were active.
              </p>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Total students</span>
                <strong>{students.length}</strong>
              </div>
              <div className="appsStatChip">
                <span>Active today</span>
                <strong>{stats.today}</strong>
              </div>
              <div className="appsStatChip">
                <span>Active this week</span>
                <strong>{stats.week}</strong>
              </div>
              <div className="appsStatChip">
                <span>Idle</span>
                <strong>{stats.idle}</strong>
              </div>
              <div className="appsStatChip">
                <span>Dormant</span>
                <strong>{stats.dormant}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard">
            <div className="appsSearch input-group">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="search"
                placeholder="Search name, email, course, or phone..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <div className="appsFilterGroup">
              <Form.Select
                className="filterSelect"
                value={activityFilter}
                onChange={(event) => setActivityFilter(event.target.value)}
              >
                <option value="All">All activity levels</option>
                <option value="Active today">Active today</option>
                <option value="Active this week">Active this week</option>
                <option value="Idle">Idle</option>
                <option value="Dormant">Dormant</option>
              </Form.Select>
            </div>

            <div className="incomeToolbarMeta activityLogsMeta">
              <span className="incomeToolbarText">{filteredStudents.length} visible students</span>
              <span className="incomeInlineDot"></span>
              <span className="incomeToolbarText">Based on Android sign-in metadata</span>
            </div>

            <Button variant="outline-dark" onClick={fetchAndroidAppStudents} disabled={loading}>
              <FontAwesomeIcon icon={faArrowsRotate} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="incomeStateCard">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading Android app students...</span>
              </Spinner>
              <p>Loading Android app activity...</p>
            </div>
          ) : (
            <div className="appsTableCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faMobileScreenButton} className="me-2" />
                  Android app students
                </h6>
                <span>{filteredStudents.length} results</span>
              </div>

              <div className="appsTableWrap">
                <Table hover responsive className="appsTable">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Last Android Login</th>
                      <th>Activity</th>
                      <th>Days Since Login</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => {
                      const activityProfile = getActivityProfile(student.lastLoginDate);

                      return (
                        <tr key={student.id}>
                          <td>
                            <div className="appsStudentCell">
                              <div className="appDefProfPic">
                                <img
                                  src={student.profilePic || defaulProfPic}
                                  alt="profile"
                                />
                              </div>
                              <div>
                                <p className="appsStudentName">{student.bio}</p>
                                <span className="appsStudentMeta">{student.email || student.userId}</span>
                              </div>
                            </div>
                          </td>
                          <td>{student.course}</td>
                          <td>{formatDateTime(student.lastLoginDate)}</td>
                          <td>
                            <Badge bg={activityProfile.tone}>{activityProfile.label}</Badge>
                            <div className="small text-muted mt-1">{activityProfile.details}</div>
                          </td>
                          <td>
                            {activityProfile.daysSince === null
                              ? 'Not recorded'
                              : formatRelativeDays(activityProfile.daysSince)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="appsViewBtn"
                              onClick={() => navigate('/view-student', { state: { myData: student.userId } })}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredStudents.length === 0 && (
                      <tr>
                        <td colSpan="6" className="appsEmptyState">
                          No Android app students match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AndroidAppActivity;
